/**
 * ClientContactCard - Card "Cliente & Contato" da sidebar T1.
 *
 * Contato: dados reais de `ticket.contactId`, cruzando o `contact` já
 * populado no ticket (id/nome/foto) com `contacts/dropdown` (rota cross-module
 * existente, sem permissionMiddleware) pra completar e-mail/telefone.
 *
 * Cliente (Plano C, Task 5): Select habilitado via `useCustomersDropdown`
 * (`customers/dropdown`, cross-module sem permissionMiddleware). Sem gate a
 * `isOpen`/popover — este card não fica atrás de um popover como
 * Responsável/Equipe (`PeopleCard`), fica sempre visível na sidebar da
 * página do ticket, que só monta quando o usuário navega pra `/tickets/:id`
 * (1 fetch por visita, mesmo padrão não-gated já usado aqui pra
 * `useContactsDropdown`).
 */
import React, { useMemo } from 'react';
import { Select } from '@/shared/components/ui';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { useContactsDropdown } from '@/modules/contacts/hooks/useContactsDropdown';
import { useCustomersDropdown } from '@/modules/crm/customers/hooks';
import { SidebarCard } from './SidebarCard';
import { SidebarField } from '../SidebarField';
import type { PopulatedTicket } from '../../types';
import { Icon } from '@/shared/icons';

export interface ClientContactCardProps {
  ticket: PopulatedTicket;
  /** Troca (ou `''` para limpar) o vínculo `ticket.customerId` via `PUT /tickets/:id`. */
  onCustomerChange: (customerId: string) => void;
  loading?: boolean;
}

export const ClientContactCard: React.FC<ClientContactCardProps> = ({ ticket, onCustomerChange, loading = false }) => {
  const { data: contactsDropdown } = useContactsDropdown();
  const { data: customersDropdown, isLoading: isLoadingCustomers } = useCustomersDropdown();

  const contactExtra = ticket.contactId
    ? contactsDropdown?.find((c) => c.id === ticket.contactId)
    : undefined;

  const contactName = ticket.contact?.name || contactExtra?.name;
  const hasContact = !!ticket.contactId && !!contactName;

  const customerOptions = useMemo(
    () => [
      { value: '', label: isLoadingCustomers ? 'Carregando...' : '— Sem cliente —' },
      ...(customersDropdown ?? []).map((c) => ({ value: c.id, label: c.name })),
    ],
    [customersDropdown, isLoadingCustomers]
  );

  return (
    <SidebarCard icon="briefcase" title="Cliente & Contato" collapsible data-testid="ticket-detail-client-contact-card">
      {/* Cliente (Plano C) */}
      <div className="py-1.5" style={{ borderBottom: '1px solid var(--theme-card-border, #f1f5f9)' }}>
        <Select
          label="Cliente"
          value={ticket.customerId || ''}
          onChange={onCustomerChange}
          options={customerOptions}
          disabled={loading}
          data-testid="ticket-customer-select"
        />
      </div>

      {/* Contato real do ticket */}
      <SidebarField label="Contato" data-testid="ticket-detail-contact-field">
        {hasContact ? (
          <div className="flex items-center gap-1.5">
            <Avatar className="w-[18px] h-[18px]">
              {ticket.contact?.picture && <AvatarImage src={ticket.contact.picture} />}
              <AvatarFallback className="text-[8px] font-bold">
                {contactName?.charAt(0)?.toUpperCase() || '?'}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs font-medium" style={{ color: 'var(--theme-card-text)' }}>
              {contactName}
            </span>
          </div>
        ) : (
          <>
            <Icon name="user" className="w-3.5 h-3.5" style={{ color: 'var(--theme-muted-text)' }} />
            <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>Sem contato</span>
          </>
        )}
      </SidebarField>

      {hasContact && contactExtra?.email && (
        <SidebarField label="E-mail" data-testid="ticket-detail-contact-email-field">
          <span className="text-xs" style={{ color: 'var(--theme-card-text)' }}>{contactExtra.email}</span>
        </SidebarField>
      )}

      {hasContact && contactExtra?.phone && (
        <SidebarField label="Telefone" data-testid="ticket-detail-contact-phone-field">
          <span className="text-xs" style={{ color: 'var(--theme-card-text)' }}>{contactExtra.phone}</span>
        </SidebarField>
      )}
    </SidebarCard>
  );
};

ClientContactCard.displayName = 'ClientContactCard';
